import axios from 'axios';
import { Order } from '../types';
import { baseUrl } from '../utils/config';

// Use the centralized config
const API_BASE_URL = baseUrl;

// Logo URL for email templates
const LOGO_URL = 'https://tachafy.com/wp-content/uploads/2025/01/image3.png';

class EmailService {
  private api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }
  });

  constructor() {
    // Add authentication interceptor
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        config.headers = config.headers || {};
        (config.headers as any).Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  private fromEmail = 'contact@tachafy.com';
  private fromName = 'Tachafy Skin Survey';
  
  /**
   * Send an email
   * 
   * @param to Recipient email address
   * @param subject Email subject
   * @param emailData Data to be used in the email template
   * @param emailType Type of email to send (determines which template to use)
   * @param forceSend Optional parameter to force sending even in development mode
   * @returns Promise with the response
   */
  private async sendEmail(
    to: string,
    subject: string,
    emailData: Record<string, any>,
    emailType: 'order_confirmation' | 'order_status' | 'test' | 'custom' = 'custom',
    forceSend: boolean = false
  ): Promise<any> {
    // Skip sending emails in development mode unless forceSend is true
    if (process.env.NODE_ENV === 'development' && !forceSend) {
      console.log('Skipping email in development mode. Set forceSend=true to override.');
      console.log('Would have sent email to:', to);
      console.log('Subject:', subject);
      console.log('Email data:', emailData);
      return {
        success: true,
        message: 'Email skipped in development mode'
      };
    }
    
    try {
      console.log(`Sending real email to ${to}${process.env.NODE_ENV === 'development' ? ' (forced in dev mode)' : ''}...`);
      
      // Use the API endpoint directly
      console.log(`Using API endpoint: ${API_BASE_URL}/email`);
      
      // Ensure all required fields are present
      if (!to || !subject) {
        throw new Error('Missing required fields: to and subject are required');
      }
      
      // Check if there's a prescription PDF and handle it properly
      if (emailData && emailData.prescriptionPdf) {
        console.log('Email contains prescription PDF attachment');
        
        // If it's a data URI, extract just the base64 part
        if (typeof emailData.prescriptionPdf === 'string' && emailData.prescriptionPdf.startsWith('data:')) {
          emailData.prescriptionPdf = emailData.prescriptionPdf.split(',')[1];
          console.log('Extracted base64 data from data URI');
        }
        
        // Check if the PDF data is too large (over 5MB)
        const pdfSizeInMB = (typeof emailData.prescriptionPdf === 'string' ? 
          (emailData.prescriptionPdf.length * 3/4) / (1024 * 1024) : 0);
          
        if (pdfSizeInMB > 5) {
          console.warn(`Prescription PDF is too large (${pdfSizeInMB.toFixed(2)}MB), removing attachment`);
          delete emailData.prescriptionPdf;
        } else {
          console.log(`Prescription PDF size: ${pdfSizeInMB.toFixed(2)}MB`);
        }
      }
      
      // Log the data being sent (without large attachments in the log)
      const logData = { ...emailData };
      if (logData.prescriptionPdf) {
        logData.prescriptionPdf = '[PDF DATA]';
      }
      
      console.log('Sending email with data:', {
        to,
        subject,
        emailType,
        emailData: JSON.stringify(logData).substring(0, 100) + '...' // Log truncated version for readability
      });
      
      // Set a longer timeout for requests with attachments
      const timeout = emailData && emailData.prescriptionPdf ? 30000 : 10000;
      
      const response = await axios.post(`${API_BASE_URL}/email`, {
        to: to,
        subject: subject,
        emailType: emailType,
        emailData: emailData,
        fromEmail: this.fromEmail,
        fromName: this.fromName
      }, {
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: timeout
      });
      
      console.log('Email sent successfully:', response.data);
      return response.data;
    } catch (err: any) {
      console.error('Error sending email:', err);
      
      let errorMessage = 'Unknown error occurred';
      
      if (err.response) {
        errorMessage = `Server returned ${err.response.status}: ${err.response.statusText}`;
        console.log('Response data:', err.response.data);
      } else if (err.request) {
        errorMessage = 'No response received from server. This could be due to CORS issues.';
      } else {
        errorMessage = err.message;
      }
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Send an email directly to the API
   * 
   * @param to Recipient email address
   * @param subject Email subject
   * @param emailData Data to be used in the email template
   * @param emailType Type of email to send (determines which template to use)
   * @param forceSend Optional parameter to force sending even in development mode
   * @returns Promise with the response
   */
  async sendEmailDirect(
    to: string, 
    subject: string, 
    emailData: Record<string, any>, 
    emailType: 'order_confirmation' | 'order_status' | 'test' | 'custom' = 'custom',
    forceSend: boolean = false
  ): Promise<any> {
    // Skip sending emails in development mode unless forceSend is true
    if (process.env.NODE_ENV === 'development' && !forceSend) {
      console.log('Skipping direct email in development mode. Set forceSend=true to override.');
      console.log('Would have sent email directly to:', to);
      console.log('Subject:', subject);
      console.log('Email data:', emailData);
      return {
        success: true,
        message: 'Direct email skipped in development mode'
      };
    }
    
    try {
      console.log(`Sending real email directly to ${to}${process.env.NODE_ENV === 'development' ? ' (forced in dev mode)' : ''}...`);
      
      // Use the API endpoint directly
      console.log(`Using API endpoint: ${API_BASE_URL}/email`);
      
      // Ensure all required fields are present
      if (!to || !subject) {
        throw new Error('Missing required fields: to and subject are required');
      }
      
      // Check if there's a prescription PDF and handle it properly
      if (emailData && emailData.prescriptionPdf) {
        console.log('Direct email contains prescription PDF attachment');
        
        // If it's a data URI, extract just the base64 part
        if (typeof emailData.prescriptionPdf === 'string' && emailData.prescriptionPdf.startsWith('data:')) {
          emailData.prescriptionPdf = emailData.prescriptionPdf.split(',')[1];
          console.log('Extracted base64 data from data URI');
        }
        
        // Check if the PDF data is too large (over 5MB)
        const pdfSizeInMB = (typeof emailData.prescriptionPdf === 'string' ? 
          (emailData.prescriptionPdf.length * 3/4) / (1024 * 1024) : 0);
          
        if (pdfSizeInMB > 5) {
          console.warn(`Prescription PDF is too large (${pdfSizeInMB.toFixed(2)}MB), removing attachment`);
          delete emailData.prescriptionPdf;
        } else {
          console.log(`Prescription PDF size: ${pdfSizeInMB.toFixed(2)}MB`);
        }
      }
      
      // Log the data being sent (without large attachments in the log)
      const logData = { ...emailData };
      if (logData.prescriptionPdf) {
        logData.prescriptionPdf = '[PDF DATA]';
      }
      
      console.log('Sending direct email with data:', {
        to,
        subject,
        emailType,
        emailData: JSON.stringify(logData).substring(0, 100) + '...' // Log truncated version for readability
      });
      
      // Set a longer timeout for requests with attachments
      const timeout = emailData && emailData.prescriptionPdf ? 30000 : 10000;
      
      const response = await axios.post(`${API_BASE_URL}/email`, {
        to: to,
        subject: subject,
        emailType: emailType,
        emailData: emailData,
        fromEmail: this.fromEmail,
        fromName: this.fromName
      }, {
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: timeout
      });
      
      console.log('Email sent directly successfully:', response.data);
      return response.data;
    } catch (err: any) {
      console.error('Error sending email directly:', err);
      
      let errorMessage = 'Unknown error occurred';
      
      if (err.response) {
        errorMessage = `Server returned ${err.response.status}: ${err.response.statusText}`;
        console.log('Response data:', err.response.data);
      } else if (err.request) {
        errorMessage = 'No response received from server. This could be due to CORS issues.';
      } else {
        errorMessage = err.message;
      }
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Send an order confirmation email
   * 
   * @param order The order data
   * @param forceSend Optional parameter to force sending even in development mode
   * @param customEmailData Optional custom email data to override the default
   * @returns Promise with the response
   */
  async sendOrderConfirmation(
    order: Order,
    forceSend: boolean = false,
    customEmailData?: Record<string, any>
  ): Promise<any> {
    const to = order.customerInfo.email;
    
    // Extract just the numeric part of the order ID for a cleaner subject line
    const orderIdDisplay = order.id.replace('order_', '');
    const subject = `Your Tachafy Order #${orderIdDisplay}`;
    
    // Ensure we have all required data
    if (!to) {
      console.error('Cannot send order confirmation: Missing customer email');
      throw new Error('Missing customer email');
    }
    
    console.log(`Preparing order confirmation email for order ${order.id} to ${to}`);
    
    // Calculate delivery fee from order data
    const subtotal = order.products.reduce((sum, product) => sum + (product.price * product.quantity), 0);
    const deliveryFee = order.totalAmount - subtotal;
    
    const emailData = customEmailData || {
      order: {
        id: order.id,
        createdAt: order.createdAt,
        totalAmount: order.totalAmount,
        products: order.products,
        status: order.status,
        delivery_fee: deliveryFee
      },
      customer: order.customerInfo,
      year: new Date().getFullYear(),
      logoUrl: LOGO_URL
    };

    // Log the email data being sent
    console.log('Order confirmation email data:', {
      to,
      subject,
      emailType: 'order_confirmation',
      dataKeys: Object.keys(emailData),
      hasAttachment: customEmailData && 'prescriptionPdf' in customEmailData
    });

    return await this.sendEmail(to, subject, emailData, 'order_confirmation', forceSend);
  }

  /**
   * Send an order status update email
   * 
   * @param order The order data
   * @param oldStatus The previous order status
   * @param forceSend Optional parameter to force sending even in development mode
   * @returns Promise with the response
   */
  async sendOrderStatusUpdate(order: Order, oldStatus: Order['status'], forceSend: boolean = false): Promise<any> {
    const to = order.customerInfo.email;
    
    // Ensure we have all required data
    if (!to) {
      console.error('Cannot send status update: Missing customer email');
      throw new Error('Missing customer email');
    }

    // Extract just the numeric part of the order ID for a cleaner subject line
    const orderIdDisplay = order.id.replace('order_', '');
    const subject = `Your Tachafy Order #${orderIdDisplay} Status Update`;
    
    console.log(`Preparing order status update email for order ${order.id} to ${to}`);
    
    // Create a data object with all the information needed for the email template
    const emailData = {
      order: {
        id: order.id,
        createdAt: order.createdAt,
        totalAmount: order.totalAmount,
        products: order.products,
        status: order.status
      },
      oldStatus: oldStatus,
      statusMessage: this.getStatusUpdateMessage(order.status, oldStatus),
      customer: order.customerInfo,
      year: new Date().getFullYear(),
      logoUrl: LOGO_URL
    };

    // Log the email data being sent
    console.log('Order status update email data:', {
      to,
      subject,
      emailType: 'order_status',
      dataKeys: Object.keys(emailData)
    });

    return await this.sendEmail(to, subject, emailData, 'order_status', forceSend);
  }
  
  /**
   * Get the appropriate message based on the order status
   * 
   * @param newStatus The new order status
   * @param oldStatus The previous order status
   * @returns HTML message
   */
  private getStatusUpdateMessage(newStatus: Order['status'], oldStatus: Order['status']): string {
    switch (newStatus) {
      case 'approved':
        return `We're pleased to inform you that we've started processing your order. We'll let you know once it's ready for shipping.`;
      case 'shipped':
        return `Great news! Your order has been shipped and is on its way to you.`;
      case 'completed':
        return `Your order has been delivered. We hope you enjoy your products!`;
      case 'cancelled':
        return `Your order has been cancelled. If you didn't request this cancellation, please contact our customer service.`;
      default:
        return `Your order status has been updated from "${this.capitalizeFirstLetter(oldStatus)}" to "${this.capitalizeFirstLetter(newStatus)}".`;
    }
  }

  /**
   * Capitalize the first letter of a string
   * 
   * @param text The text to capitalize
   * @returns The capitalized text
   */
  private capitalizeFirstLetter(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  /**
   * Send an order confirmation email directly (bypassing the normal flow)
   * 
   * @param order The order data
   * @returns Promise with the response
   */
  async sendOrderConfirmationDirect(order: Order): Promise<any> {
    const to = order.customerInfo.email;
    
    // Extract just the numeric part of the order ID for a cleaner subject line
    const orderIdDisplay = order.id.replace('order_', '');
    const subject = `Your Tachafy Order #${orderIdDisplay}`;
    
    // Ensure we have all required data
    if (!to) {
      console.error('Cannot send order confirmation: Missing customer email');
      throw new Error('Missing customer email');
    }
    
    console.log(`Preparing direct order confirmation email for order ${order.id} to ${to}`);
    
    // Create a data object with all the information needed for the email template
    const emailData = {
      order: {
        id: order.id,
        createdAt: order.createdAt,
        totalAmount: order.totalAmount,
        products: order.products,
        status: order.status
      },
      customer: order.customerInfo,
      year: new Date().getFullYear(),
      logoUrl: LOGO_URL
    };

    // Log the email data being sent
    console.log('Direct order confirmation email data:', {
      to,
      subject,
      emailType: 'order_confirmation',
      dataKeys: Object.keys(emailData)
    });

    return await this.sendEmailDirect(to, subject, emailData, 'order_confirmation', true);
  }

  /**
   * Send a test email to verify email functionality (instance method)
   * 
   * @param to Recipient email address
   * @returns Promise with the response
   */
  async sendTestEmail(to: string, forceSend: boolean = false): Promise<any> {
    console.log(`Attempting to send test email to ${to}...`);
    
    const subject = 'Test Email from Tachafy';
    
    // Create a data object with test information
    const emailData = {
      timestamp: new Date().toLocaleString(),
      testId: 'test-' + Date.now(),
      year: new Date().getFullYear(),
      logoUrl: LOGO_URL
    };
    
    // Use the sendEmail method which now uses the email proxy
    return await this.sendEmail(to, subject, emailData, 'test', forceSend);
  }
  
  /**
   * Send a test email to verify email functionality (static method)
   * 
   * @param to Recipient email address
   * @param forceSend Optional parameter to force sending even in development mode
   * @returns Promise with the response
   */
  static async sendTestEmail(to: string, forceSend: boolean = false): Promise<any> {
    console.log('Static method: Attempting to send test email...');
    
    // In development mode, simulate successful email sending unless forceSend is true
    if (process.env.NODE_ENV === 'development' && !forceSend) {
      console.log('DEV MODE: Simulating successful test email:');
      console.log('To:', to);
      console.log('NOTE: Set forceSend=true to send real emails in development mode');
      return {
        success: true,
        message: 'Test email simulated in development mode',
        id: 'test-static-' + Date.now(),
        provider: 'Microsoft 365 (simulated)',
        details: {
          to: to,
          subject: 'Test Email from Tachafy',
          fromEmail: 'contact@tachafy.com',
          fromName: 'Tachafy Skin Survey'
        }
      };
    }
    
    try {
      console.log(`Sending real test email to ${to}${process.env.NODE_ENV === 'development' ? ' (forced in dev mode)' : ''}...`);
      
      const subject = 'Test Email from Tachafy';
      
      // Create a data object with test information
      const emailData = {
        timestamp: new Date().toLocaleString(),
        testId: 'test-static-' + Date.now(),
        year: new Date().getFullYear(),
        logoUrl: LOGO_URL
      };
      
      console.log(`Sending test email to: ${to} via API endpoint: ${API_BASE_URL}/email`);
      
      // Create a temporary instance to use the authenticated API
      const tempInstance = new EmailService();
      const response = await tempInstance.api.post(`/email`, {
        to,
        subject,
        emailType: 'test',
        emailData,
        fromEmail: 'contact@tachafy.com',
        fromName: 'Tachafy Skin Survey'
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Error in static sendTestEmail:', error);
      
      if (error.response) {
        console.log('Response data:', error.response.data);
        console.log('Response status:', error.response.status);
        
        // Provide more specific error messages based on status code
        if (error.response.status === 403) {
          throw new Error('Server returned Forbidden (403). This may be due to CORS issues or server configuration.');
        } else if (error.response.status === 404) {
          throw new Error('Server returned Not Found (404). The email endpoint could not be found.');
        } else {
          throw new Error(`Server error: ${error.response.status}`);
        }
      } else if (error.request) {
        console.log('No response received:', error.request);
        if (error.message?.includes('Network Error')) {
          throw new Error('Network Error: This could be due to CORS issues. Check server CORS configuration.');
        } else {
          throw new Error('No response from server');
        }
      } else {
        throw new Error(`Error: ${error.message}`);
      }
    }
  }
}

// Add default export
export default EmailService;